import type React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type { Similarity } from '@vlabs/api/luna-api/model/Similarity';
import { useGetCards } from '@vlabs/api/point-api/client';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import ListLink from '@vlabs/shared/legacy-components/list-link/ListLink';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { FaceSample, Tag } from '@vlabs/uikit';

import { GoToCardLink } from '@vlabs/monokit/features/go-to-card';

import { useEventCardTranslation } from '../i18n';
import st from './shared.module.sass';

interface PersonMatchCardProps {
  externalId: string;
  similarity?: Similarity;
}

function PersonMatchCard({
  externalId,
  similarity = undefined,
}: PersonMatchCardProps): React.JSX.Element | null {
  const { t } = useEventCardTranslation();
  const { data: cards } = useGetCards(
    { card_ids: [externalId], targets: ['card_id', 'main_face_id', 'user_data', 'lists', 'create_time'] },
    { query: { enabled: !!externalId } },
  );
  const card = cards?.cards?.[0];

  const { data: attributeSamples } = useGetFaceAttributeSamples(
    card?.main_face_id ?? '',
    { query: { enabled: !!card?.main_face_id } },
  );

  if (!card) return null;

  const sampleSrc = getLunaSampleUrl('faces', attributeSamples?.samples[0]);
  const fio = card.user_data?.fio as string | undefined;

  return (
    <div
      className={st.Card}
      data-testid="search.result.faceCard"
    >
      <div
        className={st.PrimarySection}
        data-testid="search.result.primarySection"
      >
        <FaceSample
          className={st.Sample}
          src={sampleSrc}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {similarity != null && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="primary"
              size="large"
              title={t('similarity_card')}
              value={similarity}
            />
          )}
          <div
            className={st.Date}
            data-testid="search.result.date"
          >
            {formatDateTime(card.create_time, DATE_FORMATS.withYear)}
          </div>
        </div>
      </div>
      <div
        className={st.SecondarySection}
        data-testid="search.result.secondarySection"
      >
        <div className={st.RowItem}>
          <div
            className={st.RowTitle}
            data-testid="search.result.rowTitle"
          >
            {t('fio')}
          </div>
          <div className={st.RowValue}>{fio ?? '-'}</div>
        </div>
        <div className={st.RowItem}>
          <div
            className={st.RowTitle}
            data-testid="search.result.rowTitle"
          >
            {t('lists')}
          </div>
          <div className={st.RowValue}>
            <ListLink lists={card.lists} />
          </div>
        </div>
      </div>
      <GoToCardLink
        cardId={externalId}
        className={st.Link}
        data-testid="search.result.detailsLink"
      />
    </div>
  );
}

export { PersonMatchCard };
